import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
const ARGENTINE_NAMES = ['María L.', 'Laura M.', 'Gabriela P.', 'Silvina R.', 'Mónica G.', 'Carla F.', 'Patricia S.', 'Verónica T.', 'Andrea B.', 'Florencia D.', 'Romina V.', 'Cecilia A.', 'Marcela O.', 'Adriana C.', 'Liliana E.', 'Susana N.', 'Claudia H.', 'Daniela Q.', 'Valeria K.', 'Soledad J.', 'Paola I.', 'Natalia U.', 'Mariana Z.', 'Estela Y.', 'Roxana W.', 'Beatriz X.', 'Inés P.', 'Graciela R.', 'Alejandra M.', 'Norma L.'];
const ARGENTINE_PROVINCES = ['Buenos Aires', 'CABA', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Neuquén', 'Bahía Blanca', 'Resistencia', 'Posadas', 'Paraná', 'Corrientes', 'San Luis', 'Jujuy', 'Río Negro', 'Chubut', 'Catamarca'];
const PRODUCTS = [{
  title: 'Método Wellaging 360°',
  edition: 'Sistema Completo + 4 Bonuses',
  price: '$17.990',
  image: 'https://cdn.magicpatterns.com/uploads/cKPHZGzEjSA7MPjy1gxhWe/portada_ebook_PRINCIPAL.png',
  weight: 80
}, {
  title: 'Wellaging + Rituales',
  edition: 'Sistema + Audios de Bienestar',
  price: '$22.980',
  image: 'https://cdn.magicpatterns.com/uploads/1mVrRURW6fjS5nj3jtn2zi/PORTADA_DE_ORDER_BUMP.png',
  weight: 20
}];
interface Notification {
  id: number;
  name: string;
  city: string;
  product: (typeof PRODUCTS)[0];
}
function pickWeighted<T extends {
  weight: number;
}>(items: T[]): T {
  const total = items.reduce((sum, item) => sum + item.weight, 0);
  let random = Math.random() * total;
  for (const item of items) {
    random -= item.weight;
    if (random <= 0) return item;
  }
  return items[0];
}
function pickRandom<T>(arr: T[], excludeList: T[] = []): T {
  const available = arr.filter(item => !excludeList.includes(item));
  const pool = available.length > 0 ? available : arr;
  return pool[Math.floor(Math.random() * pool.length)];
}
export function RecentPurchasePopup() {
  const [current, setCurrent] = useState<Notification | null>(null);
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const prefersReducedMotion = useRef(typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let hideTimeoutId: ReturnType<typeof setTimeout>;
    const showNotification = () => {
      const name = pickRandom(ARGENTINE_NAMES, recentNamesRef.current);
      const city = pickRandom(ARGENTINE_PROVINCES);
      const product = pickWeighted(PRODUCTS);
      // Update recent names rolling history (last 10)
      recentNamesRef.current = [name, ...recentNamesRef.current].slice(0, 10);
      idRef.current += 1;
      setCurrent({
        id: idRef.current,
        name,
        city,
        product
      });
      // Hide after 4 seconds
      hideTimeoutId = setTimeout(() => {
        setCurrent(null);
        // Schedule next: random 8–15 s
        const nextDelay = 8000 + Math.random() * 7000;
        timeoutId = setTimeout(showNotification, nextDelay);
      }, 4000);
    };
    // First notification after 3 seconds
    timeoutId = setTimeout(showNotification, 3000);
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(hideTimeoutId);
    };
  }, []);
  const animProps = prefersReducedMotion.current ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.2
    }
  } : {
    initial: {
      opacity: 0,
      y: 60
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 60
    },
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  };
  return <div className="fixed bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-[60] pointer-events-none px-4 w-full max-w-md" data-id="element-435">
      <AnimatePresence mode="wait" data-id="element-436">
        {current && <motion.div key={current.id} {...animProps} className="pointer-events-auto bg-white/95 backdrop-blur-md border border-gold/20 rounded-2xl shadow-2xl p-3 md:p-4 flex items-center gap-3 md:gap-4" data-id="element-437">
            {/* Product image */}
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden bg-cream-dark shrink-0 border border-gold/10" data-id="element-438">
              <img src={current.product.image} alt={current.product.title} className="w-full h-full object-cover" data-id="element-439" />
            </div>

            {/* Content */}
            <div className="flex-grow min-w-0" data-id="element-440">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-441">
                <ShoppingBag className="w-3 h-3 text-olive" data-id="element-442" />
                <p className="text-[10px] uppercase tracking-widest text-olive font-medium" data-id="element-443">
                  Compra Reciente
                </p>
              </div>
              <p className="text-sm text-charcoal font-light leading-snug" data-id="element-444">
                <span className="font-medium" data-id="element-445">{current.name}</span> en{' '}
                <span className="font-medium" data-id="element-446">{current.city}</span> compró{' '}
                <span className="font-serif italic text-olive" data-id="element-447">
                  {current.product.title}
                </span>{' '}
                —{' '}
                <span className="font-semibold text-gold-dark" data-id="element-448">
                  {current.product.price}
                </span>
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}